import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { JournalHoldingsTreeVolume } from "@/lib/api/catalog";
import { formatNumberWithCommas } from "@/lib/formatters";
import { JournalHoldingRows } from "./JournalHoldingRows";

interface JournalHoldingsTreeProps {
  volumes: JournalHoldingsTreeVolume[];
}

function VolumeRow({ volume }: { volume: JournalHoldingsTreeVolume }) {
  const [isOpen, setIsOpen] = useState(false);
  const contentId = `journal-volume-${volume.id}`;

  return (
    <Card className="overflow-hidden">
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-between rounded-none px-4 py-4 text-left"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="space-y-1">
          <span className="block font-semibold">
            <span className="text-muted-foreground">Volume: </span>
            {volume.volumeNumber}
          </span>
          <span className="block text-xs font-normal text-muted-foreground">
            {volume.publicationYear ? (
              <>
                <span>Publication year: {volume.publicationYear}</span>
                <span aria-hidden="true"> · </span>
              </>
            ) : null}
            <span>
              Copies: {formatNumberWithCommas(volume.copyCount)}{" "}
              {volume.copyCount === 1 ? "copy" : "copies"}
            </span>
          </span>
        </span>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        )}
      </Button>
      {isOpen ? (
        <div id={contentId} className="border-t">
          <JournalHoldingRows issues={volume.issues} />
        </div>
      ) : null}
    </Card>
  );
}

export function JournalHoldingsTree({ volumes }: JournalHoldingsTreeProps) {
  if (volumes.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        No journal holdings have been received yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" aria-label="Journal holdings">
      {volumes.map((volume) => (
        <VolumeRow key={volume.id} volume={volume} />
      ))}
    </div>
  );
}
